/**
 * Adds View Transitions to in-app link clicks when the browser supports
 * `document.startViewTransition`. Otherwise links behave exactly as before.
 * Routing itself is unchanged — we only wrap the same navigate() call.
 */
import { useEffect } from "react";
import { flushSync } from "react-dom";
import { useNavigate } from "react-router-dom";

type DocWithVT = Document & { startViewTransition?: (cb: () => void) => unknown };

export function ViewTransitionLinks() {
  const navigate = useNavigate();

  useEffect(() => {
    const doc = document as DocWithVT;
    if (typeof doc.startViewTransition !== "function") return;

    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = (e.target as Element | null)?.closest?.("a");
      if (!a || a.target || a.hasAttribute("download") || a.dataset.noTransition !== undefined) return;
      const url = new URL(a.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      const to = url.pathname + url.search + url.hash;
      if (to === window.location.pathname + window.location.search + window.location.hash) return;
      e.preventDefault();
      doc.startViewTransition!(() => {
        flushSync(() => navigate(to));
      });
    };

    // Capture phase: runs before React Router's <Link> handler, which then sees
    // defaultPrevented and skips its own navigate (no double navigation).
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [navigate]);

  return null;
}
